// src/components/BrainHealthView.jsx

import React, { useState, useEffect } from 'react';
import {
    Brain, Activity, Utensils, Moon, Users, Heart,
    CheckCircle2, ChevronDown, Info, FileText, Play, X, ExternalLink
} from 'lucide-react';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import CustomYouTubePlayer from '../shared/CustomYouTubePlayer';
import { useOrganization } from '../context/OrganizationContext';

// B2 fix: resource rows were styled as clickable but had no onClick.
const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
    return (match && match[2].length === 11) ? match[2] : null;
};

const PILLARS = [
    {
        id: 'physical',
        tag: 'Physical', 
        title: "Physical Activity",
        icon: Activity,
        color: "text-red-600",
        bg: "bg-red-50",
        border: "border-red-100",
        desc: "Exercise increases blood flow to the brain and encourages the growth of new brain cells.",
        tips: ["Aim for 150 mins of moderate activity per week.", "Include strength training twice a week.", "Move your body in new ways to challenge coordination."]
    },
    {
        id: 'stimulation',
        tag: 'Stimulation',
        title: "Mental Stimulation",
        icon: Brain,
        color: "text-purple-600",
        bg: "bg-purple-50",
        border: "border-purple-100",
        desc: "Lifelong learning builds 'cognitive reserve,' making the brain more resilient.",
        tips: ["Learn a new skill or language.", "Solve puzzles or play strategy games.", "Read challenging books or articles."]
    },
    {
        id: 'nutrition',
        tag: 'Nutrition',
        title: "Nutrition",
        icon: Utensils,
        color: "text-green-600",
        bg: "bg-green-50",
        border: "border-green-100",
        desc: "The brain requires high-quality fuel to function and repair itself.",
        tips: ["Eat a Mediterranean-style diet (rich in plants, healthy fats).", "Stay hydrated; dehydration impacts focus.", "Limit processed sugars which can cause inflammation."]
    },
    {
        id: 'sleep',
        tag: 'Sleep',
        title: "Sleep & Relaxation",
        icon: Moon,
        color: "text-indigo-600",
        bg: "bg-indigo-50",
        border: "border-indigo-100",
        desc: "Sleep is when the brain clears out toxins and consolidates memories.",
        tips: ["Maintain a consistent sleep schedule.", "Create a wind-down routine without screens.", "Aim for 7-9 hours of quality rest."]
    },
    {
        id: 'social',
        tag: 'Social',
        title: "Social Connection",
        icon: Users,
        color: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-100",
        desc: "Social isolation is a major risk factor for cognitive decline.",
        tips: ["Schedule regular catch-ups with friends.", "Join a club or group with shared interests.", "Volunteer or mentor to stay engaged."]
    },
    {
        id: 'stress',
        tag: 'Stress',
        title: "Stress Management",
        icon: Heart,
        color: "text-pink-600",
        bg: "bg-pink-50",
        border: "border-pink-100",
        desc: "Chronic stress releases cortisol, which can damage the brain over time.",
        tips: ["Practice mindfulness or deep breathing.", "Set healthy boundaries to protect your energy.", "Seek professional support when overwhelmed."]
    }
];

// --- HELPER TO GET ORG ID ---
const getUserOrgId = async (uid) => {
    try {
        const snap = await getDoc(doc(db, "users", uid));
        return snap.exists() ? snap.data().organizationId : null;
    } catch { return null; }
};

const BrainHealthView = ({ onAskAi }) => {
    // Private resources belong to the org on screen (the previewed org for a
    // super-admin in preview mode), not the viewer's own org.
    const { orgData } = useOrganization();
    const [activePillar, setActivePillar] = useState(null);
    const [globalResources, setGlobalResources] = useState([]);
    const [privateResources, setPrivateResources] = useState([]);
    // B2: the resource being viewed in the modal (videos); non-video resources
    // with a URL open in a new tab instead.
    const [activeResource, setActiveResource] = useState(null);

    const openResource = (res) => {
        if (res.type === 'Video' && getYouTubeVideoId(res.url)) {
            setActiveResource(res);
        } else if (res.url) {
            window.open(res.url, '_blank', 'noopener,noreferrer');
        }
    };
    
    // 1. Fetch Global Resources (NHL Approved)
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "brain_health_resources"), (snap) => {
            setGlobalResources(snap.docs.map(d => ({ id: d.id, ...d.data(), isPrivate: false })));
        });
        return () => unsub();
    }, []);

    // 2. Fetch Private Resources (If User has Org)
    useEffect(() => {
        const fetchPrivate = async () => {
            const user = auth.currentUser;
            if (!user) return;
            const orgId = orgData?.id || await getUserOrgId(user.uid);
            if (!orgId) return;

            const unsub = onSnapshot(doc(db, "organizations", orgId, "private_libraries", "brain_health_resources"), (docSnap) => {
                if (docSnap.exists()) {
                    setPrivateResources(docSnap.data().resources?.map(r => ({ ...r, isPrivate: true })) || []);
                }
            });
            return () => unsub();
        };
        fetchPrivate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgData?.id]);

    // 3. Merge Lists
    const allResources = [...globalResources, ...privateResources];

    // Helper to filter by tag
    const getPillarResources = (tag) => {
        return allResources.filter(r => r.tags && Array.isArray(r.tags) && r.tags.includes(tag));
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in pb-20">

            {/* B2: VIDEO PLAYER MODAL — mirrors the TopicView resource modal */}
            {activeResource && (
                <div className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setActiveResource(null)}>
                    <div className="bg-white w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                                <span className="p-2 bg-red-50 text-red-500 rounded-lg shrink-0"><Play size={16} fill="currentColor" /></span>
                                <span className="truncate">{activeResource.title}</span>
                            </h3>
                            <div className="flex items-center gap-1 shrink-0">
                                {activeResource.url && (
                                    <a href={activeResource.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-200 rounded-full transition-colors" title="Open on YouTube"><ExternalLink size={18} className="text-slate-500" /></a>
                                )}
                                <button onClick={() => setActiveResource(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors" aria-label="Close"><X size={20} className="text-slate-500" /></button>
                            </div>
                        </div>
                        <div className="w-full aspect-video bg-black">
                            <CustomYouTubePlayer videoId={getYouTubeVideoId(activeResource.url)} />
                        </div>
                    </div>
                </div>
            )}

            {/* HERO SECTION */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 text-blue-300 font-bold uppercase tracking-widest text-xs">
                            <Brain size={14} /> Brain Approved
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black mb-4">The Six Pillars of Brain Health</h1>
                        <p className="text-slate-300 text-lg leading-relaxed">
                            Optimizing performance and longevity starts here. Explore the six evidence-based pillars that form the foundation of a resilient mind.
                        </p>
                    </div>
                </div>
            </div>

            {/* STACKED PILLARS LIST */}
            <div className="space-y-4">
                {PILLARS.map((pillar) => {
                    const pillarResources = getPillarResources(pillar.tag);
                    const isOpen = activePillar === pillar.id;

                    return (
                        <div 
                            key={pillar.id} 
                            className={`bg-white rounded-2xl border transition-all overflow-hidden ${isOpen ? `shadow-lg ring-1 ring-black/5 ${pillar.border}` : 'border-slate-200 hover:border-slate-300'}`}
                        >
                            {/* HEADER (Click to Expand) */}
                            <button 
                                onClick={() => setActivePillar(isOpen ? null : pillar.id)}
                                className="w-full flex items-center justify-between p-6 text-left"
                            >
                                <div className="flex items-center gap-5">
                                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${pillar.bg} ${pillar.color}`}>
                                        <pillar.icon size={28} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900">{pillar.title}</h3>
                                        <p className="text-slate-500 text-sm mt-1 font-medium">{pillar.desc}</p>
                                    </div>
                                </div>
                                <div className={`p-2 rounded-full transition-transform duration-300 ${isOpen ? 'rotate-180 bg-slate-100' : ''}`}>
                                    <ChevronDown size={20} className="text-slate-400" />
                                </div>
                            </button>

                            {/* EXPANDED CONTENT */}
                            {isOpen && (
                                <div className="px-6 pb-8 animate-in slide-in-from-top-2">
                                    <div className="w-full h-px bg-slate-100 mb-6"></div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        
                                        {/* LEFT COL: TIPS */}
                                        <div>
                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <CheckCircle2 size={14} /> Quick Actions
                                            </h4>
                                            <ul className="space-y-3">
                                                {pillar.tips.map((tip, idx) => (
                                                    <li key={idx} className="flex items-start gap-3 text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${pillar.color.replace('text-', 'bg-')}`}></div>
                                                        <span className="leading-relaxed">{tip}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* RIGHT COL: DYNAMIC RESOURCES */}
                                        <div>
                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <FileText size={14} /> Featured Resources
                                            </h4>
                                            
                                            {pillarResources.length > 0 ? (
                                                <div className="space-y-3">
                                                    {pillarResources.map((res, idx) => (
                                                        <div key={idx} onClick={() => openResource(res)} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group">
                                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${res.type === 'Video' ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-500'}`}>
                                                                {res.type === 'Video' ? <Play size={16} fill="currentColor" /> : <FileText size={16} />}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h5 className="font-bold text-slate-800 text-sm truncate group-hover:text-blue-700">
                                                                    {res.title}
                                                                    {res.isPrivate && <span className="ml-2 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full uppercase tracking-wide">Team</span>}
                                                                </h5>
                                                                <p className="text-xs text-slate-400">{res.type}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="p-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                                    <p className="text-sm text-slate-400 italic">No resources added yet.</p>
                                                    <p className="text-[10px] text-slate-300 mt-1">Admins: Add items with tag "{pillar.tag}"</p>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* INFO FOOTER */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 flex items-start gap-4">
                <div className="p-2 bg-white rounded-lg text-blue-600 shadow-sm"><Info size={20}/></div>
                <div>
                    <h4 className="font-bold text-blue-900 text-sm mb-1">Why this matters?</h4>
                    <p className="text-blue-800/80 text-sm">
                        Research shows that lifestyle factors can account for up to 40% of dementia cases. By taking small steps in these six areas, you actively build a stronger, healthier brain for the future.
                    </p>
                </div>
            </div>

        </div>
    );
};

export default BrainHealthView;